import { buffers, channel, multicastChannel } from 'redux-saga';
import { call, fork, put, SagaGenerator, select, take, takeEvery } from 'typed-redux-saga';

import { getConferenceConfig } from '../../../pages/call/selectors';
import { DisconnectPayloadError, DisconnectReason } from '../../../pages/call/types';
import { ErrorPageType } from '../../components/error-page/types';
import { ChannelHolder } from '../../helpers/channelHolder';
import { createLogger } from '../../helpers/loggerFacade';
import { cloudApi } from '../../helpers/transport';
import { getConfig, getRoomAgentConfig, isRoomAgentMode, selectErrorPageType } from '../config/selectors';
import { Connection } from './Connection';
import { HaltReason } from './constants';
import { RoomAgentHaltError } from './errors';
import { RecorderManager } from './recorder/RecorderManager';
import { RecordHelpers } from './recorder/sagas';
import { connectionSaga } from './sagas/connection';
import { debugSaga } from './sagas/debug';
import { dtmfSaga } from './sagas/dtmf';
import { mediaSaga } from './sagas/media';
import { recordSaga } from './sagas/record';
import { statsSaga } from './sagas/stats';
import { SetDescriptionEvent, SignalingAPI } from './SignalingAPI';
import { Description, HaltDescriptor, RtcEvent, SDKAdapter } from './types';

const logger = createLogger('room-agent-saga');

export function* setRoomAgentToken(): SagaGenerator<void> {
    const { roomAgent } = yield* select(getConfig);

    if (roomAgent && !roomAgent.dropHeader) {
        const tokenHeaderName =
            roomAgent.version === 2 ? 'X-Telemost-Meeting-Room-Agent-Token' : 'X-Telemost-Room-Agent-Token';
        cloudApi.setHeader(tokenHeaderName, roomAgent.token);
    }
}

export function mapConfErrorToHaltDescriptor(error: DisconnectPayloadError): HaltDescriptor {
    const reason = (() => {
        switch (error.reason) {
            case DisconnectReason.UNATHORIZED:
                return HaltReason.ACCESS_FORBIDDEN;
            default:
                return HaltReason.UNKNOWN_ERROR;
        }
    })();

    return {
        reason,
        description: error.description,
    };
}

export function* ensurePageLoadedSaga() {
    if (!(yield* select(isRoomAgentMode))) {
        return;
    }

    const errorPageType = yield* select(selectErrorPageType);

    if (!errorPageType) {
        return;
    }

    const reason = (() => {
        switch (errorPageType) {
            case ErrorPageType.USER_KECKED:
            case ErrorPageType.USER_KICKED_WITH_BAN:
            case ErrorPageType.UNAUTHORIZED:
            case ErrorPageType.FORBIDDEN:
            case ErrorPageType.ORGANIZATION_MISMATCHES:
                return HaltReason.ACCESS_FORBIDDEN;
            case ErrorPageType.NOT_FOUND:
                return HaltReason.INVALID_MEETING_ROOM_NUMBER;
            default:
                return HaltReason.UNKNOWN_ERROR;
        }
    })();

    global.window?.ROOM_AGENT_halt({
        reason,
        description: `telemost open error ${errorPageType}`,
    });
}

/**
 * Инициализация room agent-а
 */
export function* rootRoomAgentSaga(adapter: SDKAdapter, helpers: RecordHelpers, debugMode?: boolean) {
    logger.debug('init');

    const channelHolder = new ChannelHolder();
    const signaling = new SignalingAPI(debugMode);

    try {
        // Т.к. connection saga должна обрабатывать события последовательно, делаем для нее отдельный канал.
        const setDescriptionChannelForConnectingSaga = channel<SetDescriptionEvent>(buffers.expanding());
        // Для все других потребителей тоже делаем отдельный канал. Канал из signaling использовать не стоит,
        // т.к. eventChannel это всегда single cast канал.
        const setDescriptionChannelCommon = channel<SetDescriptionEvent>(buffers.expanding());
        yield* takeEvery(signaling.setDescriptionEventChannel, function* (event) {
            // Пишем в оба канала
            yield* put(setDescriptionChannelForConnectingSaga, event);
            yield* put(setDescriptionChannelCommon, event);
        });

        logger.debug('ready sending');
        yield* call([signaling, signaling.ready]);

        // Ждем первого события, т.к. в нем описание sdp секций
        const event = yield* take(setDescriptionChannelCommon);

        const config = yield* select(getRoomAgentConfig);
        const conferenceConfig = yield* select(getConferenceConfig);

        const recorderManager = new RecorderManager({
            adapter,
            defaultH264ProfileLevelId: config?.h264ProfileLevelId,
            sections: event?.data?.meta?.sdpMSections,
        });

        const connection = new Connection({
            recorderManager,
            bandwidth: config?.bandwidth,
            xGoogleBitrate: config?.xGoogleBitrate,
            connectingTimeout: config?.connectingTimeout,
        });

        // Создаем multicast channel чтобы можно было обрабатывать треки в нескольких местах.
        // Последовательная обработка здесь не важна, поэтому можем сделать multicast без буфера.
        const onRemoteTrackChannel = multicastChannel<{ track: MediaStreamTrack }>();
        yield* channelHolder.takeEvery(connection.onRemoteTrack, function* ({ track }) {
            yield* put(onRemoteTrackChannel, { track });
        });

        const onRtcEventChannel = multicastChannel<RtcEvent>();
        yield* channelHolder.takeEvery(connection.onRtcEvent, function* (rtcEvent) {
            yield* put(onRtcEventChannel, rtcEvent);
        });

        const onIncomingSdpChannel = multicastChannel<Description>();
        yield* channelHolder.takeEvery(connection.onIncomingSdp, function* (sdp) {
            yield* put(onIncomingSdpChannel, sdp);
        });
        const onOutgoingSdpChannel = multicastChannel<Description>();
        yield* channelHolder.takeEvery(connection.onOutgoingSdp, function* (sdp) {
            yield* put(onOutgoingSdpChannel, sdp);
        });

        yield* fork(connectionSaga, {
            setDescriptionChannel: setDescriptionChannelForConnectingSaga,
            onOutgoingSdpChannel,
            signaling,
            adapter,
            connection,
            channelHolder,
            logger,
        });
        yield* fork(mediaSaga, {
            callMetaData: event.data?.meta,
            adapter,
            logger,
            recorderManager,
            onRemoteTrackChannel,
            audioProcessingEnabled: config?.audioProcessingEnabled,
        });
        yield* fork(recordSaga, { recorderVideo: recorderManager.getMainVideoRecorder(), helpers, logger });
        yield* fork(statsSaga, {
            adapter,
            connection,
            signaling,
            roomId: conferenceConfig?.room_id,
            peerId: conferenceConfig?.peer_id,
            channelHolder,
            onRtcEventChannel,
            onIncomingSdpChannel,
            onOutgoingSdpChannel,
        });
        yield* fork(debugSaga, { connection });
        yield* fork(dtmfSaga, { onRemoteTrackChannel });

        const disconnectDoneResult = yield* take(adapter.disconnectDoneAction);

        if (disconnectDoneResult.payload?.error) {
            const descriptor = mapConfErrorToHaltDescriptor(disconnectDoneResult.payload.error);

            throw new RoomAgentHaltError(descriptor.reason, descriptor.description, {
                method: 'rootRoomAgentSaga',
            });
        }
    } catch (error) {
        const normalizedError = RoomAgentHaltError.from(error, {
            method: 'rootRoomAgentSaga',
        });

        logger.error(normalizedError);

        yield* call([signaling, signaling.halt], {
            reason: normalizedError.reason,
            description: normalizedError.description,
        });
        yield* call([adapter, adapter.cancel]);
    } finally {
        channelHolder.close();
        logger.debug('finished');
    }
}
